import { BreakpointSizesType } from '@pickit/pickit-grid-system';
import { Icons } from '../BasicIcon/types';
import { ButtonHierarchy } from '../Button/types';
import {
  IconSizesType,
  StatusButtonType,
  StatusTagType,
  TableSizesType,
} from '../ThemeProvider/types';
import { ToggleSwitchProps } from '../ToggleSwitch/types';
import { FileType } from '../FileInput/types';

export type SharedColumnTypes = 'string' | 'icon' | 'tag' | 'array';
export type ColumnAlignmentTypes = 'center' | 'left' | 'right';
export const TABLEGUTTER = 16;
export type ColumnSizeType = { [key in BreakpointSizesType]?: number | 'auto' };

export type SharedTableProps = {
  hover?: boolean;
  cols?: number;
  selectAllEnable?: boolean;
  noResultsText?: string;
  rowCount?: number;
  size?: TableSizesType;
  fontSize?: 'small' | 'large';
  dynamicHeight?: boolean;
  testId?: string;
};

type TableInputType = {
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  showClearIcon?: boolean;
  testId?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onMouseDown?: () => void;
  onBlur?: () => void;
};

interface TextInputConfig extends TableInputType {
  pattern?: string;
  maxLength?: number;
}

interface NumericInputConfig extends TableInputType {
  negative?: boolean;
  allowDecimal?: boolean;
  leftAddOn?: string;
  rightAddOn?: string;
  maxInt?: number;
  maxDecimal?: number;
  pattern?: string;
  maxLength?: number;
}

interface SelectInputConfig extends TableInputType {
  keyProperty?: string;
  showProperty?: string;
  options: any[];
  isValueObject?: boolean;
}

interface MultiSelectInputConfig extends SelectInputConfig {
  labelAll?: string;
}

interface DatePickerConfig extends TableInputType {
  minDate?: string;
  maxDate?: string;
  outputFormat?: string;
  singleSelection?: boolean;
  disabledWeekDays?: number[];
}

interface FileInputConfig extends TableInputType {
  fileTypes: FileType[];
  multipleFiles?: boolean;
  dragable?: boolean;
  base64?: boolean;
  multiFileText?: string;
}

interface AsyncAutocompleteConfig extends SelectInputConfig {
  isLoading?: boolean;
  getOptions: (term: string) => string[];
}

export type FormatterType = ((text: string) => string)[] | ((text: string) => string);

type IconProps<T> = {
  name?: Icons;
  color?: string;
  onClick?: (row: T) => void;
};

type IconConfigType<T> = IconProps<T> & {
  size?: IconSizesType;
  variations?: { [key: string]: IconProps<T> };
  disabledCondition?: (row: T) => boolean;
};

type CheckboxConfigType<T> = {
  onChangeCheck: (row: T) => void;
  isChecked: (row: T) => boolean;
  selectAll?: () => void;
  allSelected?: () => boolean;
  isDisabled?: (row: T) => boolean;
};

type ButtonType<T> = {
  status?: StatusButtonType;
  hierarchy?: ButtonHierarchy;
  label?: string;
  onClick: (row: T) => void;
  iconName?: Icons;
  iconPosition?: 'left' | 'right';
  isMobile?: boolean;
  valueLabel?: boolean;
};

type ButtonConfig<T> = ButtonType<T> & {
  disabledCondition?: (row: T) => boolean;
  variations?: { [key: string]: ButtonType<T> };
};

type TextConfig = {
  formatter?: FormatterType;
  separator?: string;
  weight?: number;
  color?: string;
};

type TagType = {
  status?: StatusTagType;
  label?: string;
  labelKey?: string;
};

interface TagConfig extends TagType {
  variations?: { [key: string]: TagType };
  fullWidth?: boolean;
}

type ArrayConfig = {
  formatter: (array: any[]) => string;
};

export type SharedHeaderType<T> = {
  key?: string | string[];
  label?: string;
  align?: ColumnAlignmentTypes;
  showCondition?: (row: T) => boolean;
  col?: number | 'auto' | ColumnSizeType;
  testId?: string;
};

export type IconHeaderType<T> = {
  type: 'icon';
  checkboxConfig?: never;
  iconConfig: IconConfigType<T>;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type StringHeaderType = {
  type?: 'string';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: TextConfig;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type CheckboxHeaderType<T> = {
  type: 'checkbox';
  checkboxConfig: CheckboxConfigType<T>;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type ButtonHeaderType<T> = {
  type: 'button';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig: ButtonConfig<T>;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type TagHeaderType = {
  type: 'tag';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig: TagConfig;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type ArrayHeaderType = {
  type: 'array';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig: ArrayConfig;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type TextInputHeaderType = {
  type: 'textInput';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: TextInputConfig;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type NumericInputHeaderType = {
  type: 'numericInput';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: NumericInputConfig;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type SelectInputHeaderType = {
  type: 'selectInput';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig: SelectInputConfig;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type DatePickerHeaderType = {
  type: 'datePicker';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: DatePickerConfig;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type AutoCompleteHeaderType = {
  type: 'autoComplete';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig: SelectInputConfig;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type ToggleSwitchHeaderType = {
  type: 'toggleSwitch';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig: ToggleSwitchProps;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type MultiSelectInputHeaderType = {
  type: 'multiSelectInput';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig: MultiSelectInputConfig;
  fileInputConfig?: never;
  asyncAutoCompleteConfig?: never;
};

export type FileInputHeaderType = {
  type: 'fileInput';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig: FileInputConfig;
  asyncAutoCompleteConfig?: never;
};

export type AsyncAutocompleteHeaderType = {
  type: 'asyncAutocomplete';
  checkboxConfig?: never;
  iconConfig?: never;
  buttonConfig?: never;
  textConfig?: never;
  tagConfig?: never;
  arrayConfig?: never;
  selectInputConfig?: never;
  numericInputConfig?: never;
  textInputConfig?: never;
  datePickerConfig?: never;
  autoCompleteConfig?: never;
  toggleSwitchConfig?: never;
  multiSelectInputConfig?: never;
  fileInputConfig?: never;
  asyncAutoCompleteConfig: AsyncAutocompleteConfig;
};
